import { useCallback, useState } from 'react';
import awsCaptchaService from '../services/awsCaptchaService';

export const useAWSProtection = () => {
  const [isProtected, setIsProtected] = useState(true);
  const [riskLevel, setRiskLevel] = useState<'Low' | 'Medium' | 'High'>('Low');

  /**
   * Evaluates client-side risk signals and returns whether a challenge is needed.
   * Returns true when challenge is required, false when not.
   * Currently driven by awsCaptchaService risk scoring (bot detection,
   * rapid requests, device fingerprinting). Server-side enforcement
   * (WAF rate limiting, Cognito adaptive auth) operates independently.
   */
  const shouldRequireChallenge = useCallback(
    async (action: 'signin' | 'signup' | 'data_access'): Promise<boolean> => {
      if (!awsCaptchaService.isEnabled()) {
        return false;
      }

      try {
        const riskDecision = await awsCaptchaService.assessRisk(action);
        setRiskLevel(riskDecision.riskLevel);
        return riskDecision.requiresChallenge;
      } catch (error) {
        console.error('Risk assessment failed:', error);
        // Fail open — no challenge on error
        return false;
      }
    },
    []
  );

  /**
   * Builds device context (fingerprint, browser info, timestamp) for
   * Cognito Advanced Security's adaptive risk evaluation.
   * Passed to AuthenticationDetails.setContextData() during sign-in.
   */
  const getContextData = useCallback((username: string) => {
    return awsCaptchaService.getAuthContextData(username);
  }, []);

  return {
    shouldRequireChallenge,
    isProtected,
    riskLevel,
    getContextData,
  };
};
